import {
    Building2,
    CircleDollarSign,
    ClipboardCheck,
    Crown,
    Inbox,
    Landmark,
    LayoutDashboard,
    Network,
    Newspaper,
    ServerCog,
    ShieldCheck,
    UserCog,
    UserRound,
    Users,
} from "lucide-react";
import i18n from "../i18n";
import {ADMIN_ROLES, KNOWN_ADMIN_ROLES} from "../auth/roles";
import {
    canModerateBusinessOnboarding,
} from "../../features/business-onboarding/permissions";

export {ADMIN_ROLES};

const allItems = [
    {
        key: "overview",
        href: "/overview",
        icon: LayoutDashboard,
        allowedRoles: ["platform", ADMIN_ROLES.ACCOUNTANT, ADMIN_ROLES.DEPLOYER, ADMIN_ROLES.IMPLEMENTATION_HEAD],
    },
    {
        key: "organizations",
        href: "/employers",
        icon: Building2,
        allowedRoles: [
            "platform",
            ADMIN_ROLES.OWNER,
            ADMIN_ROLES.DEPLOYER,
            ADMIN_ROLES.IMPLEMENTATION_HEAD,
            ADMIN_ROLES.BRANCH,
        ],
        requiredAction: "organization.view",
    },
    {
        key: "business-onboarding",
        href: "/business-onboarding",
        icon: ClipboardCheck,
        allowedRoles: [ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR],
    },
    {
        key: "users",
        href: "/users",
        icon: Users,
        allowedRoles: ["platform"],
        requiredAction: "user.view",
    },
    {
        key: "finance",
        moduleKeys: [
            "finance",
            "partner_payments",
            "partner_remote_payments",
            "partner_refunds",
        ],
        href: "/reports",
        matchPaths: ["/finance"],
        icon: CircleDollarSign,
        allowedRoles: [
            "platform",
            ADMIN_ROLES.ACCOUNTANT,
            ADMIN_ROLES.OWNER,
            ADMIN_ROLES.BRANCH,
            ADMIN_ROLES.WORKER,
        ],
        requiredAction: "finance.view",
    },
    {
        key: "settlements",
        moduleKeys: ["settlements", "partner_payouts"],
        href: "/settlements",
        icon: Landmark,
        allowedRoles: ["platform", ADMIN_ROLES.ACCOUNTANT, ADMIN_ROLES.OWNER],
        requiredAction: "settlements.view",
    },
    {
        key: "referrals",
        href: "/referral",
        icon: Network,
        allowedRoles: ["platform", ADMIN_ROLES.OWNER, ADMIN_ROLES.DEPLOYER, ADMIN_ROLES.IMPLEMENTATION_HEAD],
        requiredAction: "referral.view",
    },
    {
        key: "club",
        href: "/club",
        icon: Crown,
        allowedRoles: ["platform"],
    },
    {
        key: "content",
        href: "/news",
        icon: Newspaper,
        allowedRoles: ["platform", ADMIN_ROLES.OWNER],
        requiredAction: "content.view",
    },
    {
        key: "inbox",
        href: "/notifications",
        matchPaths: ["/reviews"],
        icon: Inbox,
        moduleKeys: ["inbox", "partner_dashboard", "deployer_dashboard"],
        allowedRoles: ["platform", ADMIN_ROLES.OWNER, ADMIN_ROLES.DEPLOYER, ADMIN_ROLES.IMPLEMENTATION_HEAD],
    },
    {
        key: "deployers",
        href: "/deployers",
        icon: UserCog,
        allowedRoles: ["platform", ADMIN_ROLES.IMPLEMENTATION_HEAD],
    },
    {
        key: "agent",
        href: "/agent",
        icon: UserRound,
        allowedRoles: [ADMIN_ROLES.AGENT],
        moduleKeys: ["agent_dashboard"],
    },
    {
        key: "audit",
        href: "/audit",
        icon: ShieldCheck,
        allowedRoles: ["platform", ADMIN_ROLES.ACCOUNTANT],
        requiredAction: "audit.view",
    },
    {
        key: "system",
        href: "/system",
        icon: ServerCog,
        allowedRoles: [ADMIN_ROLES.SUPERADMIN],
        requiredAction: "system.manage",
    },
];

const PLATFORM = new Set([ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR]);

const roleMatches = (role, allowedRoles) =>
    allowedRoles.includes("platform")
        ? PLATFORM.has(role) || allowedRoles.includes(role)
        : allowedRoles.includes(role);

const itemEnabled = (item, {role, modules, actions}) => {
    if (!KNOWN_ADMIN_ROLES.has(role) || !roleMatches(role, item.allowedRoles)) return false;
    if (item.key === "business-onboarding") return canModerateBusinessOnboarding(role);

    const actionSet = new Set(actions || []);
    if (item.requiredAction) return actionSet.has(item.requiredAction);

    const moduleSet = new Set(modules || []);
    return (item.moduleKeys || [item.key]).some((key) => moduleSet.has(key));
};

// Подписи пунктов живут в словарях (nav.<key>.label / nav.<key>.description) и
// подставляются на каждом чтении списка: при смене языка AdminShell
// перерисовывается и получает уже переведённые пункты.
const translateItem = (item) => ({
    ...item,
    label: i18n.t(`nav.${item.key}.label`),
    description: i18n.t(`nav.${item.key}.description`),
});

const selectForCapabilities = ({role, modules = [], actions = []}) =>
    allItems.filter((item) => itemEnabled(item, {role, modules, actions}));

export const getNavigationForCapabilities = (capabilities) =>
    selectForCapabilities(capabilities).map(translateItem);

const selectForRole = (role, modules = []) =>
    allItems.filter((item) => {
        if (!KNOWN_ADMIN_ROLES.has(role) || !roleMatches(role, item.allowedRoles)) return false;
        if (item.key === "business-onboarding") return canModerateBusinessOnboarding(role);

        const moduleSet = new Set(modules || []);
        return (item.moduleKeys || [item.key]).some((key) => moduleSet.has(key));
    });

export const getNavigationForRole = (role, modules = []) =>
    selectForRole(role, modules).map(translateItem);

export const canAccessAdminSection = ({role, modules, actions, sectionKey}) =>
    selectForCapabilities({role, modules, actions})
        .some((item) => item.key === sectionKey);

export const getDefaultPathForCapabilities = (capabilities) =>
    selectForCapabilities(capabilities)[0]?.href || "/overview";

export const getDefaultPathForRole = (role, modules) => {
    if (Array.isArray(modules)) {
        return selectForRole(role, modules)[0]?.href || "/overview";
    }
    return role === ADMIN_ROLES.DEPLOYER ? "/employers" : "/overview";
};
